/*
 * @Author: Teemo Ding
 * @Date: 2021-07-09 14:47:47
 * @LastEditors: Json Chen
 * @LastEditTime: 2022-06-02 16:16:25
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Settings/Taxes/TaxEditPage/index.tsx
 */
import { Skeleton } from '@/src/components/Antd';
import Layout from '@/src/layout/EditorPageLayout/EditorPageLayout';
import TopLeft from '@/src/layout/EditorPageLayout/GoBackAndBreadCrumb/GoBackAndBreadCrumb';
import TopRight from '@/src/layout/EditorPageLayout/TopRightBtnsGroups/TopRightBtnsGroups';
import {
  deleteTax,
  getTaxDetail,
  saveTaxDetail,
} from '@/src/request/settings/tax';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { Form, message } from 'antd';
import React, { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { Type } from 'src/constrains/ProductsSelection/ProductSelectionWithHsCode';
import RouteBlock from 'src/constrains/RouterBlock/RouterBlock';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import { modal } from 'src/utils/Modal';
import TaxApplies from './TaxApplies';
import TaxRate from './TaxRate';
import TaxValidCountry from './TaxValidCountry';
import { breadcrumbData } from './config';
import css from './css.module.less';

interface Conditions {
  chargeCurrency: string;
  minimum: number;
  maximum: number;
}
interface FormData {
  ruleTitle?: string;
  conditions?: Conditions;
  rateCurrency?: string;
  initData?: string;
}

const permissions = () => {
  const view_tax_rule_details = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsTaxes,
        operation: EnumOperation.view_tax_rule_details,
      }),
    ],
  });
  const add_edit_tax_rule = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsTaxes,
        operation: EnumOperation.add_edit_tax_rule,
      }),
    ],
  });
  const delete_tax_rule = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsTaxes,
        operation: EnumOperation.delete_tax_rule,
      }),
    ],
  });
  return {
    view_tax_rule_details,
    add_edit_tax_rule,
    delete_tax_rule,
  };
};
const TaxEditPage = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { view_tax_rule_details, add_edit_tax_rule, delete_tax_rule } =
    permissions();
  const [form] = Form.useForm();
  const params: any = useParams();
  const history = useHistory();
  const { uuid } = params;
  const [loading, setLoading] = useState(true);
  const [hasModified, setHasModified] = useState(false);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [initData, setInitData] = useState<FormData>({});
  const [countryList, setCountryList] = useState([]);
  const [applyToData, setApplyToData] = React.useState({
    type: Type.HSCode,
    products: [],
    hsCodes: [],
    dangerousGoodsClasses: [],
  });
  const onFinish = (values) => {
    const conditions = {
      chargeCurrency: values.chargeCurrency,
      minimum: values.minimum,
      maximum: values.maximum,
    } as Conditions;

    saveTaxDetail({
      ...values,
      ...{ uuid },
      conditions,
      applyTo: applyToData.type,
      hsCode: {
        hsCodePrefixes: applyToData.hsCodes.map((item) => item.value),
        dangerousGoodsClasses: applyToData.dangerousGoodsClasses.map(
          (item) => item.value,
        ),
      },
      countryAreas: countryList,
      specificProducts: applyToData.products,
    })
      .then(() => {
        setHasModified(false);
        message.success(getI18n('general.savesuccess'));
      })
      .then(() => {
        goBackToList();
      });
  };

  const handleCountryListChange = (data) => {
    setCountryList(data);
    setHasModified(true);
  };

  const handleOnChange = (e = {}) => {
    const _d = { ...applyToData, ...e };
    setApplyToData(_d);
    setHasModified(true);
  };

  const Child = [
    {
      child: (
        <TaxValidCountry
          countryList={countryList}
          setCountryList={handleCountryListChange}
          disabled={!view_tax_rule_details}
          disableEdit={!add_edit_tax_rule}
        />
      ),
    },
    {
      child: (
        <TaxApplies
          data={applyToData}
          handleOnChange={handleOnChange}
          disabled={!view_tax_rule_details}
          disableEdit={!add_edit_tax_rule}
        />
      ),
    },
    {
      child: (
        <TaxRate
          availableCurrencies={availableCurrencies}
          disabled={!view_tax_rule_details}
          disableEdit={!add_edit_tax_rule}
        />
      ),
    },
  ];

  useEffect(() => {
    if (view_tax_rule_details) {
      getTaxDetail({ taxUuid: uuid }).then((data) => {
        setAvailableCurrencies(data.availableCurrencies || []);
        setInitData(data?.storeTaxSetting || {});
        setLoading(false);
        const hsCodes = data?.storeTaxSetting?.hsCode.hsCodePrefixes.map(
          (item) => ({
            key: item,
            value: item,
          }),
        );
        const dangerousGoodsClasses =
          data?.storeTaxSetting?.hsCode.dangerousGoodsClasses.map((item) => ({
            key: item,
            value: item,
          }));
        setApplyToData({
          ...applyToData,
          type: (data.storeTaxSetting || {}).applyTo || Type.All,
          hsCodes: hsCodes || [],
          dangerousGoodsClasses: dangerousGoodsClasses || [],
          products: data?.storeTaxSetting?.specificProducts || [],
        });
        setCountryList(data?.storeTaxSetting?.countryAreas);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const goBackToList = () => {
    history.push({
      pathname: '/settings/tax/list',
    });
  };
  const deleteTaxRequest = () => {
    deleteTax(uuid).then(() => {
      setHasModified(false);
      message.success(getI18n('general.deletedsuccessfully'));
      history.push({
        pathname: '/settings/tax/list',
      });
    });
  };

  const deleteTaxModal = () => {
    modal.error({
      title: getI18n('storeTaxes.taxListPage.deleteTaxRulesTitle'),
      content: getI18n('storeTaxes.taxListPage.deleteTaxRulesContent'),
      onCancelText: getI18n('general.cancel'),
      onCancel: () => {
        console.log('close');
      },
      onOk: () => deleteTaxRequest(),
      onOkText: getI18n('general.delete'),
    });
  };

  const topLeft = (
    <TopLeft
      goBack={goBackToList}
      breadcrumbData={breadcrumbData(initData?.ruleTitle, { getI18n })}
    />
  );

  const topRight = (
    <TopRight
      onDiscard={goBackToList}
      onSubmit={() => {
        form.submit();
      }}
      displayDuplicateGroup={uuid ? true : false}
      onDelete={delete_tax_rule ? () => deleteTaxModal() : null}
      onSaveDisable={!hasModified || !add_edit_tax_rule}
    />
  );
  return (
    <Fragment>
      <RouteBlock
        onConfirm={(f) => f()}
        okText={getI18n('general.leave')}
        active={hasModified}
        title={getI18n('general.unsavedchanges')}
        content={getI18n('general.leavePage')}
      />
      <Skeleton loading={loading} active>
        <Form
          className={css.taxFormContainer}
          name='texEdit'
          scrollToFirstError
          form={form}
          onFinish={onFinish}
          onValuesChange={() => {
            setHasModified(true);
          }}
          initialValues={{
            ...initData,
            rateCurrency: initData?.rateCurrency || '',
            chargeCurrency: initData?.conditions?.chargeCurrency
              ? initData?.conditions?.chargeCurrency
              : availableCurrencies.length
              ? availableCurrencies[0].code
              : '',
            applyTo: 'All',
            taxInclusion: initData?.initData || 'Exclusive',
            maximum: initData?.conditions?.maximum || null,
            minimum: initData?.conditions?.minimum || 0,
          }}>
          <Layout
            elements={Child}
            topLeft={topLeft}
            layoutLeftClassName={css.layoutLeftStyle}
            topRight={topRight}></Layout>
        </Form>
      </Skeleton>
    </Fragment>
  );
};

export default TaxEditPage;
